/**
 * Horizontal material strip (.pdp-material__tabs--scroll, 4+ materials).
 *
 * Three things `overflow-x: auto` alone doesn't give:
 *   - the active tab fully in view, since landing on the last material would
 *     otherwise show its own tab clipped at the right edge
 *   - drag-to-scroll for mouse users: a wheel scrolls the page instead of the
 *     strip, and macOS hides the scrollbar, so there is no way to reach the
 *     off-screen tabs
 *   - any sign that the strip scrolls at all: four tabs fill the strip almost
 *     exactly, leaving the fifth as a ~13px sliver that reads as a border, so
 *     the clipped edge gets a fade instead
 *
 * Loaded eagerly rather than via `data-src`: the lazy loader only fires on the
 * first interaction, and the scroll position has to be right before that.
 */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 5;
  var STRIP_SELECTOR = '.pdp-material__tabs--scroll';
  // Must match the mask stops on .pdp-material__tabs--scroll.
  var FADE_WIDTH = 24;

  function canScroll(strip) {
    return strip.scrollWidth - strip.clientWidth > 1;
  }

  // Only ever moves the strip's own scrollLeft, never the page — this template
  // has a history of scroll-restore regressions. Clears the fade as well as the
  // edge, or the tab it just revealed would sit half-dimmed under the mask.
  function revealActive(strip) {
    var active = strip.querySelector('.pdp-material__tab.is-active');
    if (!active || !canScroll(strip)) return;

    var hiddenRight = active.offsetLeft + active.offsetWidth + FADE_WIDTH - (strip.scrollLeft + strip.clientWidth);
    if (hiddenRight > 0) {
      strip.scrollLeft += hiddenRight;
    } else if (active.offsetLeft - FADE_WIDTH < strip.scrollLeft) {
      strip.scrollLeft = active.offsetLeft - FADE_WIDTH;
    }
  }

  // Fade only the sides that are actually clipping something, so the cue points
  // at where the hidden tabs are: right on landing, left once scrolled across.
  function updateFades(strip) {
    var maxScroll = strip.scrollWidth - strip.clientWidth;
    strip.classList.toggle('is-fade-start', strip.scrollLeft > 1);
    strip.classList.toggle('is-fade-end', strip.scrollLeft < maxScroll - 1);
  }

  function watchScroll(strip) {
    var queued = false;
    strip.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          updateFades(strip);
        });
      },
      { passive: true }
    );
  }

  function enableDrag(strip) {
    var startX = 0;
    var startScroll = 0;
    var maxMoved = 0;
    var pointerId = null;
    var dragging = false;

    strip.addEventListener('pointerdown', function (event) {
      // Touch and pen scroll natively; capturing them would cost the momentum.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (!canScroll(strip)) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = strip.scrollLeft;
      maxMoved = 0;
      dragging = false;
    });

    strip.addEventListener('pointermove', function (event) {
      if (pointerId === null || event.pointerId !== pointerId) return;

      var dx = event.clientX - startX;
      maxMoved = Math.max(maxMoved, Math.abs(dx));

      if (!dragging && maxMoved > DRAG_THRESHOLD) {
        dragging = true;
        strip.classList.add('is-dragging');
        // Keeps the drag alive once the pointer leaves the strip. Throws if the
        // pointer is already gone, which is harmless — the drag still tracks.
        try {
          strip.setPointerCapture(pointerId);
        } catch (e) {}
      }

      if (dragging) {
        event.preventDefault();
        strip.scrollLeft = startScroll - dx;
      }
    });

    function endDrag(event) {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      try {
        if (strip.hasPointerCapture(pointerId)) strip.releasePointerCapture(pointerId);
      } catch (e) {}
      pointerId = null;
      dragging = false;
      strip.classList.remove('is-dragging');
    }

    strip.addEventListener('pointerup', endDrag);
    strip.addEventListener('pointercancel', endDrag);

    // The tabs are real links, so a drag that ends on one must not navigate.
    // maxMoved survives until the next pointerdown, which is when click fires.
    strip.addEventListener('click', function (event) {
      if (maxMoved > DRAG_THRESHOLD) {
        event.preventDefault();
        event.stopPropagation();
      }
    });

    // Links and their icons are natively draggable and would start a ghost drag.
    strip.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  }

  function init() {
    var strips = document.querySelectorAll(STRIP_SELECTOR);
    if (!strips.length) return;

    function refresh() {
      Array.prototype.forEach.call(strips, function (strip) {
        strip.classList.toggle('is-draggable', canScroll(strip));
        revealActive(strip);
        updateFades(strip);
      });
    }

    refresh();
    Array.prototype.forEach.call(strips, function (strip) {
      enableDrag(strip);
      watchScroll(strip);
    });

    // The longest tab is content-sized, so its width moves once webfonts land.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refresh);
    }

    // Crossing 768px swaps the grid for the scroller, changing widths and overflow.
    var timer;
    window.addEventListener('resize', function () {
      clearTimeout(timer);
      timer = setTimeout(refresh, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
